import { useState, useRef, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { searchProfessions } from '@/data/professions';

/**
 * Multi-select profession search with chip/tag UI.
 * Uses onPointerDown on dropdown items to avoid blur-before-click race condition on mobile.
 */
export default function ProfessionSearch({ selected = [], onChange, onQueryChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false);

  const selectedValues = selected.map(s => s.value);

  // Notify parent of query text changes (for button enable logic)
  useEffect(() => {
    onQueryChange?.(query);
  }, [query, onQueryChange]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setFocusedIndex(-1);

    if (text.length >= 2) {
      const matches = searchProfessions(text).filter(p => !selectedValues.includes(p.value));
      setResults(matches);
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const addProfession = useCallback((profession, source = 'master_list') => {
    if (selectedValues.includes(profession.value)) return;
    const newItem = {
      name: profession.label,
      value: profession.value,
      source,
      primary: selected.length === 0,
    };
    onChange([...selected, newItem]);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setFocusedIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [selected, selectedValues, onChange]);

  const addOther = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed || selectedValues.includes(trimmed.toLowerCase())) return;
    const newItem = {
      name: trimmed,
      value: trimmed.toLowerCase(),
      source: 'user_typed',
      primary: selected.length === 0,
    };
    onChange([...selected, newItem]);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setFocusedIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [query, selected, selectedValues, onChange]);

  const removeProfession = (value) => {
    const updated = selected.filter(s => s.value !== value);
    if (updated.length > 0 && !updated.some(s => s.primary)) {
      updated[0].primary = true;
    }
    onChange(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      removeProfession(selected[selected.length - 1].value);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && focusedIndex >= 0 && focusedIndex < results.length) {
        addProfession(results[focusedIndex]);
      } else if (isOpen && focusedIndex === results.length && showOtherOption) {
        addOther();
      } else if (query.trim().length >= 2) {
        // User typed something and hit Enter — add it directly
        if (results.length > 0) {
          addProfession(results[0]);
        } else {
          addOther();
        }
      }
      return;
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (!isOpen) return;

    const totalItems = results.length + (showOtherOption ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    }
  };

  const handleBlur = () => {
    // Delay to allow pointerdown on dropdown to fire first
    setTimeout(() => {
      if (!isSelectingRef.current) {
        setIsOpen(false);
      }
      isSelectingRef.current = false;
    }, 200);
  };

  // Use pointerDown to prevent blur race condition on mobile
  const handleOptionPointerDown = (e) => {
    e.preventDefault(); // Prevents input blur
    isSelectingRef.current = true;
  };

  const showOtherOption = query.trim().length >= 2 &&
    !results.some(r => r.label.toLowerCase() === query.toLowerCase()) &&
    !selectedValues.includes(query.trim().toLowerCase());

  return (
    <div className="relative">
      {/* Chips + Input container */}
      <div
        className="bg-[#F7F4EF] px-3 py-2.5 min-h-[56px] focus-within:ring-2 focus-within:ring-[#B8985A]/30 transition-all cursor-text"
        style={{ border: '1px solid #D4C9B8', borderRadius: '2px' }}
        onClick={() => inputRef.current?.focus()}
        data-testid="profession-search-container"
      >
        <div className="flex flex-wrap gap-1.5 items-center">
          {/* Selected chips */}
          {selected.map((prof) => (
            <span
              key={prof.value}
              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full"
              style={{ backgroundColor: 'rgba(29,107,79,0.08)', color: '#1D6B4F' }}
              data-testid={`chip-${prof.value}`}
            >
              {prof.name}
              <button
                onClick={(e) => { e.stopPropagation(); removeProfession(prof.value); }}
                className="hover:bg-[#1D6B4F]/20 rounded-full p-0.5 transition-colors"
                data-testid={`remove-chip-${prof.value}`}
              >
                <X size={14} strokeWidth={2} />
              </button>
            </span>
          ))}

          {/* Input */}
          <div className="flex-1 min-w-[140px] relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (query.length >= 2) { setResults(searchProfessions(query).filter(p => !selectedValues.includes(p.value))); setIsOpen(true); } }}
              onBlur={handleBlur}
              placeholder={selected.length === 0 ? 'Start typing... e.g. hair stylist, plumber' : 'Add another...'}
              className="w-full py-1.5 px-2 text-base placeholder:text-[#8C7E6E] outline-none bg-transparent"
              data-testid="profession-search-input"
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      {/* Soft limit note */}
      {selected.length >= 5 && (
        <p className="text-xs text-[#C67A5C] mt-1.5" data-testid="many-hats-note">
          That's a lot of hats! We'll scan for all of them.
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (results.length > 0 || showOtherOption) && (
        <div
          className="absolute z-50 mt-1.5 w-full bg-[#F7F4EF] overflow-hidden max-h-64 overflow-y-auto"
          style={{ border: '1px solid #D4C9B8', boxShadow: '0 4px 20px rgba(60,50,38,0.06)', borderRadius: '2px' }}
          data-testid="profession-dropdown"
        >
          {results.map((prof, i) => (
            <button
              key={prof.value}
              onPointerDown={handleOptionPointerDown}
              onClick={() => addProfession(prof)}
              className={`w-full text-left px-5 py-3 text-sm transition-colors flex items-center justify-between ${
                focusedIndex === i ? 'bg-[#F7F4EF]' : 'hover:bg-[#F7F4EF]'
              }`}
              data-testid={`profession-option-${i}`}
            >
              <span className="font-medium" style={{ color: '#3C3226' }}>{prof.label}</span>
              <span className="text-xs capitalize" style={{ color: '#8C7E6E' }}>{prof.vertical.replace(/_/g, ' ')}</span>
            </button>
          ))}

          {showOtherOption && (
            <button
              onPointerDown={handleOptionPointerDown}
              onClick={addOther}
              className={`w-full text-left px-5 py-3 text-sm transition-colors flex items-center gap-2 ${
                focusedIndex === results.length ? 'bg-[#F7F4EF]' : 'hover:bg-[#F7F4EF]'
              }`}
              style={{ borderTop: '1px solid #D4C9B8' }}
              data-testid="profession-other-option"
            >
              <span className="font-medium" style={{ color: '#C67A5C' }}>Other:</span>
              <span style={{ color: '#3C3226' }}>{query.trim()}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
